import { isDefined } from '@aloft/toolkit-shared';
import { pdf } from '@react-pdf/renderer';

import pairedCostPDF from '../assets/export/paired-cost.pdf';
import { downloadZip } from '../modules/export/downloadZip.ts';
import { makeTkv2MarketConditionsExportDeps } from '../modules/marketConditions/makeTkv2MarketConditionsExportDeps.ts';
import { Tkv2MarketConditionsExportDeps } from '../modules/marketConditions/types.ts';
import { Tkv2SiteValueChartsParams } from '../modules/referenceView/utils/useTkv2SiteValueChartsParams.ts';
import { CostApproachExport } from '../modules/summaryV2/Export/CostApproachExport.tsx';
import { SummaryExport } from '../modules/summaryV2/Export/SummaryExport.tsx';
import { SummaryExportV2 } from '../modules/summaryV2/Export/SummaryExportV2.tsx';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../modules/summaryV2/types.ts';
import { pngHomeValueIndexTrendsChartMaker } from '../modules/summaryV2/utils/pngHomeValueIndexTrendsChartMaker.tsx';
import { pngSiteSizeToCostPerSFChartMaker } from '../modules/summaryV2/utils/pngSiteSizeToCostPerSFChartMaker.tsx';
import { pngSiteSizeToSiteValueChartMaker } from '../modules/summaryV2/utils/pngSiteSizeToSiteValueChartMaker.tsx';
import { pngSiteToValueRatioChartMaker } from '../modules/summaryV2/utils/pngSiteToValueRatioChartMaker.tsx';

type ExportableComponentProps = Tkv2PairedCostSummaryInterfaceV2;

export const handleSaveZipV2 = async (
  props: ExportableComponentProps,
  siteValueChartParams: Tkv2SiteValueChartsParams,
  addressLine: string,
): Promise<void> => {
  try {
    const files = await handleSaveZipContentV2(props, siteValueChartParams);

    await downloadZip(files, `Toolkit Depreciated Cost ${addressLine}`);
  } catch (error) {
    console.error(error);
    alert('Error generating PDF');
  }
};

async function handleSaveZipContentV2(
  props: ExportableComponentProps,
  siteValueChartParams: Tkv2SiteValueChartsParams,
) {
  const marketConditionsExportDeps = await makeTkv2MarketConditionsExportDeps({
    marketConditions: props.marketConditions,
  });
  const marketTrendsPngSource = await pngHomeValueIndexTrendsChartMaker(
    props.housingTrends?.filteredHousingTrendsData,
    props.marketConditions,
  );

  const siteSizeVsSiteValuePngSource = await pngSiteSizeToSiteValueChartMaker(siteValueChartParams);
  const siteSizeVsCostPngSource = await pngSiteSizeToCostPerSFChartMaker(siteValueChartParams);
  const siteToValueRatioPngSource = await pngSiteToValueRatioChartMaker(siteValueChartParams);

  const pairedCost = _getPairedCostExplainerBlob();
  const adjustment = _getAdjustmentBlob(
    props,
    marketConditionsExportDeps,
    marketTrendsPngSource,
    siteSizeVsSiteValuePngSource,
    siteSizeVsCostPngSource,
    siteToValueRatioPngSource,
  );
  const cost = _getCostBlob(props);

  const files = await Promise.all([pairedCost, adjustment, cost]).then((res) =>
    res.filter(isDefined),
  );
  return files;
}

async function _getAdjustmentBlob(
  props: ExportableComponentProps,
  marketConditionsExportDeps: Tkv2MarketConditionsExportDeps,
  marketTrendsPngSource: string | undefined,
  siteSizeVsSiteValuePngSource: string | undefined,
  siteSizeVsCostPngSource: string | undefined,
  siteToValueRatioPngSource: string | undefined,
) {
  const adjustmentDoc =
    props.pairedCostVersion === 'legacy' ? (
      <SummaryExport
        {...props}
        marketConditionsExportDeps={marketConditionsExportDeps}
        marketTrendsPngSource={marketTrendsPngSource}
      />
    ) : (
      <SummaryExportV2
        {...props}
        marketConditionsExportDeps={marketConditionsExportDeps}
        marketTrendsPngSource={marketTrendsPngSource}
        siteSizeVsSiteValuePngSource={siteSizeVsSiteValuePngSource}
        siteSizeVsCostPngSource={siteSizeVsCostPngSource}
        siteToValueRatioPngSource={siteToValueRatioPngSource}
      />
    );

  const asAdjustmentPdf = pdf(adjustmentDoc);
  const blob = await asAdjustmentPdf.toBlob();

  return {
    blob,
    name: `Toolkit Adjustments ${props.subjectMetadata.address}.pdf`,
  };
}

async function _getCostBlob(props: ExportableComponentProps) {
  if (props.exportableCommentaries.costApproach.omitFromExport) {
    return undefined;
  }

  const costDoc = <CostApproachExport {...props} />;
  const asCostPdf = pdf(costDoc);
  const blob = await asCostPdf.toBlob();

  return {
    blob,
    name: `Toolkit Cost ${props.subjectMetadata.address}.pdf`,
  };
}

async function _getPairedCostExplainerBlob() {
  const blob = await fetch(pairedCostPDF).then((res) => res.blob());

  return {
    blob,
    name: 'Toolkit Depreciated Cost Framework.pdf',
  };
}
